import React, { FocusEventHandler, forwardRef, FunctionComponent, KeyboardEventHandler, MouseEventHandler, PropsWithChildren, useRef } from "react";

import { bemClassNames, combineRefs, createQA, doNothing, useSetFocus } from "@multiweb/reactUtils";
import { Cursor } from "@multiweb/types/multiweb";
import { ForwardRefType, QAProps, TabIndex, TextAlign } from "@multiweb/types/react";
import { ComponentTheme } from "@multiweb/uiShared/ComponentTheme";
import { Role } from "@multiweb/uiShared/Role";
import { SpinnerComponent } from "../SpinnerComponent";
import { ButtonBackgroundColor, ButtonThemeVariant } from "./enums";
import { getTabIndexBasedOnDisabled } from "./utils";

export type ButtonProviderEventHandlerProps = {
    /** Callback volaný na kliknutí */
    onClick: MouseEventHandler<HTMLButtonElement>;
    /** Callback volaný při kliknutí uvnitř elementu */
    onMouseDown?: MouseEventHandler<HTMLButtonElement>;
    /** Callback volaný při puštění tlačítka po kliknutí */
    onMouseUp?: MouseEventHandler<HTMLButtonElement>;
    /** Callback volaný při opuštění kurzoru z elementu */
    onMouseOut?: MouseEventHandler<HTMLButtonElement>;
    /** Callback volaný při dvojitém kliknutí na element. */
    onDoubleClick?: MouseEventHandler<HTMLButtonElement>;
    /** Callback volaný při získání zaměření */
    onFocus?: FocusEventHandler<HTMLButtonElement>;
    /** Funkce volaná při ztrátě zaměření obalované oblasti */
    onBlur?: FocusEventHandler<HTMLButtonElement>;
    /** Callback volaný při stisknutí klávesy */
    onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
    /** Callback volaný při ukončení stisku klávesy */
    onKeyUp?: KeyboardEventHandler<HTMLButtonElement>;
};

export type ButtonProviderProps = PropsWithChildren<{
    /** Pozadí tlačítka */
    backgroundColor?: ButtonBackgroundColor;
    /** Příznak, zda je tlačítko zakázané */
    disabled?: boolean;
    /** Téma tlačítka */
    theme?: ComponentTheme;
    /** Popisek zobrazený po najetí myši */
    title?: string;
    /** Umožňuje nastavit tabindex - výchozí hodnota je undefined, chování tedy závisí na prohlížeči */
    tabIndex?: TabIndex;
    /** Příznak, zda je komponenta zaměřená */
    isFocused?: boolean;
    /** Stín tlačítka */
    shadow?: boolean;
    /** Ohraničení tlačítka */
    outline?: boolean;
    /** Příznak, zda je tlačítko v nevalidním stavu */
    invalid?: boolean;
    /** Zarovnání textu na tlačítku */
    textAlign?: TextAlign;
    /** Šířka tlačítka */
    width?: number | string;
    /** Výška tlačítka */
    height?: number | string;
    /** Hodnota kurzoru předávaná do tlačítka */
    cursor?: Cursor;
    /** Upravuje výchozí chování vzhledu */
    variant?: ButtonThemeVariant;
    /** Role tlačítka */
    role?: Role;
    /** Příznak, zda má tlačítko vykreslit loading indikátor */
    isLoading?: boolean;
    /** Komponenta, která zobrazuje loading indikátor. Slouží k nahrazení defaultního spinneru. */
    LoadingIndicator?: FunctionComponent;
}> & ButtonProviderEventHandlerProps & QAProps;

const classNames = bemClassNames("button-provider-component");

/** Obalující komponenta pro tvorbu tlačítek */
export const ButtonProvider: ForwardRefType<HTMLButtonElement, ButtonProviderProps> = forwardRef<HTMLButtonElement, ButtonProviderProps>(({
    backgroundColor = ButtonBackgroundColor.default,
    disabled = false,
    onClick,
    onMouseDown,
    onMouseOut,
    onMouseUp,
    onDoubleClick,
    theme = ComponentTheme.default,
    title,
    tabIndex,
    isFocused = false,
    children,
    qa,
    shadow = false,
    outline = true,
    onFocus,
    onBlur,
    onKeyUp,
    onKeyDown,
    invalid,
    cursor,
    variant = ButtonThemeVariant.default,
    textAlign,
    width,
    height,
    role,
    isLoading = false,
    LoadingIndicator = SpinnerComponent,
}, forwardRef) => {
    const innerRef = useRef<HTMLButtonElement>(null);
    const ref = combineRefs(innerRef, forwardRef);
    useSetFocus(isFocused, innerRef);

    return (
        <button
            className={classNames({ backgroundColor, theme, disabled, shadow, outline, invalid, cursor, variant, textAlign })}
            aria-label={title}
            aria-disabled={isLoading}
            tabIndex={getTabIndexBasedOnDisabled(tabIndex, disabled)}
            onClick={isLoading || disabled ? doNothing : onClick}
            style={{ width, height }}
            {...{ onMouseDown, onMouseUp, onMouseOut, onDoubleClick, title, ref, onFocus, onBlur, onKeyDown, onKeyUp, role }}
            {...createQA(qa)}
            onBlur={onBlur}
        >
            { isLoading && (
                <div className={classNames("loading-indicator")}>
                    <LoadingIndicator />
                </div>)
            }
            <span className={classNames("content", { isLoading })}>{children}</span>
        </button>
    );
});
